/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { Fingerprint, Loader2, ScanFace } from "lucide-react";
import { cn, formatter } from "@/lib/utils";

import CurrencyInput from "react-currency-input-field";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { beneCategory, meterType } from "@/constants";
import { useSelector } from "react-redux";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PostRequest } from "@/lib/http";
import { Loading, Report } from "notiflix";
import ElectricityBeneficiaries from "@/routes/dashboard/buy-electricity/ElectricityBeneficiaries";
import { useLocation, useNavigate } from "react-router-dom";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { Capacitor } from "@capacitor/core";
import { Button } from "../ui/button";

const BuyElectricityForm = ({
  metadata,
  handleSubmit,
  watch,
  setValue,
  reset,
  errors,
  register,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isMeterChecking, setIsMeterChecking] = useState(false);
  const [meterStatus, setMeterStatus] = useState(null); // 'valid', 'error', null
  const user = useSelector(state => state.user.user)
  console.log({ user })
  const platform = Capacitor.getPlatform()

  const navigate = useNavigate();

  async function authenticateWithBiometrics() {
    const result = await NativeBiometric.isAvailable();

    if (!result.isAvailable) {
      toast.warning("Biometric authentication is not available on this device. Kindly enable biometrics in your settings.");
    }

    const verified = await NativeBiometric.verifyIdentity({
      reason: "Authorize Transaction",
      title: "Electricity Token Purchase",
      subtitle: "Please authenticate to proceed with this transaction.",
    }).then(() => true)
      .catch(() => false);

    if (verified) {
      setValue("pin", user.pin.toString())
      document.querySelector("#sub").click()
    } else {
      toast.error("Authentication failed. Please try again.");
    }
  }

  const onSubmit = async (body) => {
    Loading.standard("Processing payment...");
    const { err } = await PostRequest({
      url: "/api/buyElectricity",
      body,
    });
    if (!err) {
      Report.success(
        "Success",
        "Payment successful, check transaction receipt for Token",
      );
      navigate("/dashboard/transactions");
      reset();
    } else {
      toast.error(err);
    }
    Loading.remove();
  };
  const validateMeterNumber = async () => {
    const number = watch("number");
    const type = watch("type");
    const disco = watch("disco");
    setMeterStatus(null); // Reset meter status before checking

    if (number && type && disco && number.length > 10) {
      const { data, err } = await PostRequest({
        url: "/api/validateMeter",
        body: {
          number,
          type,
          disco,
        },
        setState: setIsMeterChecking,
      });
      if (!err) {
        setMeterStatus(data);
      } else {
        toast.error(err);
      }
    } else {
      toast.error(
        "Please select a disco type, disco name and ensure meter number is at least 11 digits long",
      );
    }
  };

  const amount = watch("amount");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section className="grid grid-cols-1 gap-2">
        <div className="col-span-1 space-y-1">
          <label htmlFor="network" className="text-sm font-semibold">
            Disco Name
          </label>

          <Select
            className="focus:border-slate-40 w-full rounded-md border border-slate-400 px-3"
            value={watch("disco") ? Number(watch("disco")) : undefined}
            onValueChange={(v) => setValue("disco", v)}
          >
            <SelectTrigger className={cn("", "py-5")}>
              <SelectValue
                placeholder="Please select disco name"
                className="font-medium"
              />
            </SelectTrigger>
            <SelectContent className="lg:-top-16">
              <SelectGroup className="">
                {metadata.discos?.map(
                  (item, i) =>
                    item && (
                      <SelectItem key={i} value={i}>
                        {item}
                      </SelectItem>
                    ),
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-1 space-y-1">
          <label htmlFor="meter-type" className="text-sm font-semibold">
            Meter Type
          </label>

          <Select
            className="focus:border-slate-40 w-full rounded-md border border-slate-400 px-3"
            value={watch("type")}
            onValueChange={(v) => setValue("type", v)}
          >
            <SelectTrigger className={cn("", "py-5")}>
              <SelectValue
                placeholder="Select meter type"
                className="font-medium"
              />
            </SelectTrigger>
            <SelectContent className="lg:-top-16">
              <SelectGroup className="">
                {meterType?.map(
                  (item, i) =>
                    item && (
                      <SelectItem key={item.name} value={item.name}>
                        {item.name}
                      </SelectItem>
                    ),
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-1">
          <label htmlFor="meter_number" className="text-sm font-semibold">
            Meter Number
          </label>
          <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
            <input
              disabled={isMeterChecking}
              type="tel"
              autoComplete="off"
              inputMode="numeric"
              {...register("number", {
                required: "Meter number is required",
                pattern: {
                  value: /^[0-9]/,
                  message: "Meter Number must be a number",
                },
              })}
              className="w-full flex-1 border-none bg-transparent text-sm outline-none"
              placeholder="3233-23232-3233"
            />

            <ElectricityBeneficiaries setValue={setValue} metadata={metadata} />
          </div>

          <button
            onClick={validateMeterNumber}
            className="bg-primary mt-2 cursor-pointer rounded p-2 text-sm font-medium text-white disabled:opacity-50"
            type="button"
            disabled={isMeterChecking}
          >
            {isMeterChecking ? "Checking...." : "Validate Meter No"}
          </button>

          {meterStatus}
          <div className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="savebeneficiary"
              checked={watch("savebeneficiary")}
              onChange={() => {
                setValue("savebeneficiary", !watch("savebeneficiary"));
              }}
            />
            <label htmlFor="savebeneficiary">Save As Beneficiary</label>
          </div>

          {errors.number && (
            <span className="text-xs font-medium text-red-500">
              {errors.number.message}
            </span>
          )}
        </div>

        {meterStatus ? (
          <>
            <div className="col-span-1">
              <label htmlFor="amount" className="text-sm font-semibold">
                Amount
              </label>
              <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
                <CurrencyInput
                  prefix="₦ "
                  value={amount}
                  min={1000}
                  onValueChange={(value) => setValue("amount", value)}
                  className="w-full border-none bg-transparent text-sm outline-none"
                  placeholder="Amount"
                />
              </div>
              {errors.amount && (
                <span className="text-xs font-medium text-red-500">
                  {errors.amount.message}
                </span>
              )}
              {amount ? (
                amount < 1000 ? (
                  <span>Minimum electricity amount is ₦1,000</span>
                ) : (
                  <span>
                    Total Amount Including Charges: ₦
                    {formatter.format(
                      Number(amount) +
                      Math.min(
                        amount * (metadata.charges / 100),
                        metadata.max_charge,
                      ),
                    )}
                  </span>
                )
              ) : (
                <></>
              )}
            </div>

            <div className="col-span-1">
              <label htmlFor="pin" className="text-sm font-semibold">
                Transaction Pin
              </label>
              <div className="flex gap-2 items-center">
                <div className="dark:border-darkBorder dark:bg-darkInput mt-1 flex items-center gap-x-3 rounded-md border bg-gray-100 p-2 py-2.5">
                  <input
                    type="password"
                    {...register("pin", {
                      required: "Pin is required",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Please enter numbers only",
                      },
                    })}
                    autoComplete="off"
                    inputMode="numeric"
                    className="w-full border-none bg-transparent text-sm outline-none"
                    placeholder="Enter pin"
                  />
                </div>
                {platform != "web" && <Button onClick={authenticateWithBiometrics} type="button" className={"h-[40px] text-white"}>
                  {platform == "android" ? <Fingerprint size={30} /> : <ScanFace size={30} />} Use Biometrics
                </Button>}
              </div>
              {errors.pin && (
                <span className="text-xs font-medium text-red-500">
                  {errors.pin.message}
                </span>
              )}
            </div>

            <button
              type="submit"
              id="sub"
              disabled={isLoading || amount < 1000 || !amount}
              className={cn(
                "btn mt-2 w-full rounded-md py-3 font-semibold text-white disabled:pointer-events-none disabled:bg-gray-600",
                {
                  "bg-primary": isLoading,
                },
              )}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" />
                  Purchasing...
                </span>
              ) : amount ? (
                "Purchase"
              ) : (
                "Select Amount"
              )}
            </button>
          </>
        ) : (
          <div className="text-center font-medium text-red-500">
            Please validate meter Number to continue
          </div>
        )}
      </section>
    </form>
  );
};

export default BuyElectricityForm;
